import PageContainer from "../PageContainer";
import { ValueForm } from "./ValueForm";
import { useMemo, useState } from "react";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { editValue, fetchValues } from "../../../services/valueService";
import { Link, Navigate, useParams } from "react-router-dom";
import { valuesPageCache } from "../../../pages/cache/PageCache";
import { usePagedQuery } from "../../../contexts/hooks/usePagedQuery";
import {
  Stack,
  Tooltip,
  IconButton,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { localizeField } from "../../../config/localization";

const EditValue = () => {
  const { showNotification } = usePreload();
  const [updating, setUpdating] = useState(false);
  const { id: valueCode } = useParams();

  const {
    data: rows,
    reload,
    fetched,
    isLoading,
    error,
  } = usePagedQuery({
    fetchFunction: fetchValues,
    searchOption: [{ id: "valueCode", display: "Code" }],
    cache: valuesPageCache,
    language: "all",
    searchKey: valueCode,
    captureErrors: true,
  });

  const row = useMemo(() => {
    return {
      ...rows[0],
      valueName: localizeField(rows, "valueName"),
      valueDisplayName: localizeField(rows, "valueDisplayName"),
      valueDescription: localizeField(rows, "valueDescription"),
      valueToolTip: localizeField(rows, "valueToolTip"),
    };
  }, [rows]);

  if (!valueCode || (fetched && !rows[0]?.valueCode)) {
    return <Navigate to="/notfound" replace />;
  }

  const onUpdate = async (updatedRow) => {
    try {
      setUpdating(true);
      const response = await editValue(updatedRow);
      if (response instanceof Response && !response.ok)
        throw new Error("Save failed");
      valuesPageCache.clear();

      showNotification(`${updatedRow?.valueCode} was updated!`, "success");
      return true;
    } catch (e) {
      console.log(e);
      showNotification(
        `something went wrong when updating the ${updatedRow?.valueCode} value.`,
        "error",
      );
      return false;
    } finally {
      setUpdating(false);
    }
  };
  return (
    <PageContainer
      title="Edit Value"
      subtitle="Update the symbol and localized details for this ETIM value"
      breadcrumbs={[
        { title: "ETIM Values", path: "/valuesnew" },
        { title: "Edit" },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Back" placement="bottom" enterDelay={300}>
            <span>
              <IconButton
                size="small"
                aria-label="Back to values"
                component={Link}
                to="/valuesnew"
              >
                <KeyboardBackspaceIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      }
    >
      {error ? (
        <Alert
          severity="error"
          action={<Button onClick={() => reload(true)}>Retry</Button>}
        >
          Unable to load value.
        </Alert>
      ) : isLoading || !fetched ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress aria-label="Loading value" />
        </Stack>
      ) : (
        <ValueForm
          onSave={onUpdate}
          onRefresh={() => reload(true)}
          props={{
            row,
            uploading: updating,
            saveText: "Save Changes",
            isUpdate: true,
          }}
        />
      )}
    </PageContainer>
  );
};

export default EditValue;
